import L from 'leaflet';
import { resolveUrl } from '../api';
import { PIN_CONFIG, toPinType, type PinType } from './constants';
import { uprightStyle } from './mapGeometry';

export function escapePinLabelHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Alla pinnar (pass, åtel, observation m.m.) samma storlek som tidigare passmarkering (12×16). Etikett på pass styrs av `passShowLabel`. */
export function createPinIcon(rawType: PinType, label?: string | null, options?: { passShowLabel?: boolean }) {
  const type = toPinType(rawType);
  const cfg = PIN_CONFIG[type];
  const isPass = type === 'pass';
  const w = 12;
  const h = 16;
  const strokeW = 1;
  const innerR = 2.5;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="${w}" height="${h}">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${cfg.color}" stroke="white" stroke-width="${strokeW}"/>
    <circle cx="12" cy="12" r="${innerR}" fill="white" opacity="0.9"/>
  </svg>`;
  const showLabel = isPass
    ? !!(options?.passShowLabel && label && String(label).trim())
    : !!(label && String(label).trim());
  const labelTop = 17;
  const labelFs = 8;
  const labelPad = '1px 4px';
  const labelHtml = showLabel
    ? `<div style="position:absolute;top:${labelTop}px;left:50%;transform:translateX(-50%);max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:rgba(0,0,0,0.65);color:white;font-size:${labelFs}px;font-weight:600;padding:${labelPad};border-radius:2px;pointer-events:none;letter-spacing:0.3px">${escapePinLabelHtml(String(label))}</div>`
    : '';
  const totalW = showLabel ? Math.max(w, 72) : w;
  const totalH = showLabel ? Math.max(h, labelTop + 12) : h;
  const pinLeft = (totalW - w) / 2;
  // QV2: i kompassläget vrids kartan med CSS; nålen vrids tillbaka (UPRIGHT_VAR) kring spetsen = ankaret, så att
  // den och etiketten står upprätt och spetsen stannar på sin position.
  return L.divIcon({
    html: `<div style="position:relative;width:${totalW}px;height:${totalH}px;${uprightStyle(totalW / 2, h)}"><div style="position:absolute;left:${pinLeft}px;top:0;width:${w}px;height:${h}px">${svg}</div>${labelHtml}</div>`,
    className: '',
    iconSize: [totalW, totalH],
    iconAnchor: [totalW / 2, h],
    popupAnchor: [0, -h],
  });
}

// GPX parsing
export function parseGpx(xmlText: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');
  const ns = 'http://www.topografix.com/GPX/1/1';

  const getEl = (parent: Element | Document, tag: string) =>
    parent.querySelector(tag) || parent.getElementsByTagNameNS(ns, tag)[0] || parent.getElementsByTagName(tag)[0];
  const getAllEl = (parent: Element | Document, tag: string) =>
    Array.from(parent.getElementsByTagNameNS(ns, tag).length > 0
      ? parent.getElementsByTagNameNS(ns, tag)
      : parent.getElementsByTagName(tag));

  const waypoints = getAllEl(doc, 'wpt').map(wpt => ({
    lat: parseFloat(wpt.getAttribute('lat') || '0'),
    lng: parseFloat(wpt.getAttribute('lon') || '0'),
    name: getEl(wpt as Element, 'name')?.textContent?.trim() || '',
    type: getEl(wpt as Element, 'type')?.textContent?.trim() || 'other',
    desc: getEl(wpt as Element, 'desc')?.textContent?.trim() || '',
  }));

  const tracks = getAllEl(doc, 'trk').map(trk => ({
    name: getEl(trk as Element, 'name')?.textContent?.trim() || '',
    type: getEl(trk as Element, 'type')?.textContent?.trim() || '',
    points: getAllEl(trk as Element, 'trkpt').map(pt => ({
      lat: parseFloat(pt.getAttribute('lat') || '0'),
      lng: parseFloat(pt.getAttribute('lon') || '0'),
    })),
  }));

  return { waypoints, tracks };
}

export function wehuntTypeToPinType(type: string, name: string): PinType {
  if (type === 'pass') return 'pass';
  if (type === 'bait') return 'atel';
  if (type === 'custom') {
    const l = name.toLowerCase();
    if (l.includes('spår') || l.includes('observation')) return 'observation';
    return 'ovrigt';
  }
  return 'ovrigt';
}

export function mediaPublicUrl(u: string | null | undefined): string {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return resolveUrl(u);
}

/** Publik sida för ett fällt djur (QR-koden på kartnålen pekar hit). */
export const VILT_PUBLIC_ORIGIN = 'https://qvenari.com';
export function viltPublicUrl(pinId: string): string {
  return `${VILT_PUBLIC_ORIGIN}/vilt/${encodeURIComponent(pinId)}`;
}

